import type { CSSProperties, Ref } from 'react';
import { cn } from '~/lib/utils';
import styles from './SlideArt.module.css';

/**
 * The eight artifacts the landing's feature rows have open, authored at 1280x720 in their own space.
 *
 * Declared inside `AgentAsksDemo` until a second row needed them (Beka, 2026-09-12) — the drawings
 * and the palette are that file's, moved rather than rewritten, so row 1 shows exactly what it did.
 * The same eight are drawn twice: scaled right down for a directory row's plate, and full size on a
 * preview stage, so a thumbnail is the artifact rather than a picture of one.
 */

export type SlideKind = 'trio' | 'metrics' | 'words' | 'flow' | 'pair' | 'mosaic' | 'figure' | 'plan';

/** The site's own palette, used the way the reference deck uses its brand colours. */
export const INK = '#1B1B1F';
export const PAPER = '#FCFBF9';
export const PINK = '#DE5A8E';
export const BLUE = '#5EA3E8';
export const SAND = '#F0C9A0';
export const MOSS = '#6A9E32';
export const SUN = '#E8C447';

/*
 * ── THE METRICS SLIDE ─────────────────────────────────────────────────────────────────────────
 *
 * Artifact 02 of the Product v2 case: signup completion before the redesign, at launch, and now, as
 * three cards with a figure and a bar each. Laid out to the reference Beka gave for it (2026-09-15),
 * measured off it at 1280x720, and it replaces the cover this slot held, the nine-becoming-three disc.
 *
 * COLOURED IN THE WORKSPACE ARTIFACT'S BLUE STYLE (Beka, 2026-09-16) — the churn chart in
 * `OneWorkspaceV2`, whose palette is defined on its `.split`: an electric cobalt page, white type,
 * translucent white for surfaces, and one lime. The reference's grey page and white cards went with
 * it. The bars keep the chart's reading of time: white at a third for before the release, white for
 * the release, lime for the month worth pointing at. The serif stays — row 3's restyle is the draft
 * headline joining the face the figures are already set in.
 *
 * The words are the case's, and they agree with the rest of the deck: nine steps became three,
 * completion went from 32% to 71% — signups doubled — and the build took the eleven weeks
 * `figure` ("What it cost") says it did.
 */

/** What the headline says before anyone edits it: the history, which is what row 3's note objects to. */
export const METRICS_HEADLINE = 'What happened to signups after we launched';

/** The launch card's line. */
export const METRICS_LAUNCH_LINE = 'Product v2 launches with three steps.';

/** The slide's ink, and the serif its figures are set in — both exported for the row that restyles the headline. */
export const METRICS_INK = '#FFFFFF';
export const METRICS_SERIF = "'Playfair Display', Georgia, serif";

/**
 * THE PLAIN LOOK: the same slide as a first draft would set it (Beka, 2026-09-16). Row 3 opens on it
 * so that the note asking for the brand has something to change: a grey page and white cards (the
 * reference's own, from before the slide went blue), dark ink, the app's sans for the figures, and
 * every bar in one grey. Nothing in it is wrong; it is just nobody's.
 *
 * `brand` is the blue look above and stays the default, so the rows that pass nothing — row 1's
 * outline — are untouched. The plain ink is exported for the same reason `METRICS_INK` is.
 */
export type MetricsLook = 'brand' | 'plain';
export const METRICS_PLAIN_INK = '#252C28';
const METRICS_PLAIN_BAR = '#B8B5B0';

/**
 * The three readings. `fill` is the bar, as a share of its track — the reading itself, so the bars
 * are the data and not a picture of it. The tones are the workspace chart's: white at a third for
 * where it started, white for the launch, lime for now.
 */
const METRIC_CARDS = [
  { label: 'June', meta: '9 steps', value: '32%', fill: 32, tone: 'rgba(255, 255, 255, 0.32)', line: 'The old flow. Most people quit at the address form.' },
  { label: 'August', meta: '3 steps', value: '58%', fill: 58, tone: '#FFFFFF', line: METRICS_LAUNCH_LINE },
  { label: 'October', meta: '3 steps', value: '71%', fill: 71, tone: '#D6FF3F', line: 'Double where we started, and still climbing.' },
] as const;

/**
 * A live restyle of the metrics slide's headline, for the row that EDITS one.
 *
 * Only the `metrics` kind reads it, and only the properties given: a row that passes nothing gets the
 * artifact exactly as authored. The type here is the text toolbar's own vocabulary — family, weight,
 * px size, colour — so the demo can hand the toolbar's state straight through without translating.
 */
export interface SlideHeadlineStyle {
  text?: string;
  fontFamily?: string;
  fontWeight?: number | string;
  fontSize?: number;
  color?: string;
}

/**
 * A kit's colours, for an artifact drawn in someone else's brand.
 *
 * Omit it and every kind is exactly as authored — this is additive, and the three existing call
 * sites pass nothing. `words` and `figure` read it; the other six keep their literals until
 * something needs them.
 */
export interface SlidePalette {
  paper: string;
  ink: string;
  accent: string;
  note: string;
}

export interface SlideArtProps {
  kind: SlideKind;
  /** Metrics only. Omit for the artifact as authored. */
  headline?: SlideHeadlineStyle;
  /**
   * Metrics only. A row that draws chrome AROUND the headline — an edit ring, an annotation rect —
   * has to know where it is, and the honest answer is the element's own box rather than a number
   * written down next to a font size that the row is about to change.
   */
  headlineRef?: Ref<HTMLDivElement>;
  /** Metrics only: the first-draft look or the brand's. Omit for the brand's. */
  look?: MetricsLook;
  /** Recolour the artifact into a brand's palette. */
  palette?: SlidePalette;
}

export function SlideArt({ kind, headline, headlineRef, look = 'brand', palette }: SlideArtProps) {
  const paper = palette?.paper ?? PAPER;
  const ink = palette?.ink ?? INK;
  /* The two colours the STYLESHEET owns, handed over as variables so the rules keep their defaults
     for every caller that passes no palette. */
  const vars = { '--slide-paper': paper, '--slide-ink': ink } as CSSProperties;
  if (kind === 'metrics') {
    /*
     * Every part is placed, and nothing is in flow: the headline is the one thing on this slide a row
     * changes the size of, and a headline that grows must not push the cards down under the notes
     * and the ring that are measuring them. It grows into the room kept above the cards for its
     * largest setting — two lines at 56.
     *
     * The headline's live values are runtime ones (the toolbar is driving them), so they stay on
     * the element; the per-card fill and tone are the card's data, handed to the stylesheet as
     * variables. Everything else is in `.metrics*`, and the plain look is `.metricsPlain` over it.
     */
    const plain = look === 'plain';
    return (
      <div
        className={cn(styles.slide, styles.metrics, plain && styles.metricsPlain)}
        style={
          {
            '--metrics-ink': plain ? METRICS_PLAIN_INK : METRICS_INK,
            '--metrics-serif': METRICS_SERIF,
          } as CSSProperties
        }
      >
        <div
          ref={headlineRef}
          className={cn(styles.metricsHeadline, headline && styles.slideEditable)}
          style={{
            ...(headline?.color ? { color: headline.color } : {}),
            ...(headline?.fontFamily ? { fontFamily: headline.fontFamily } : {}),
            ...(headline?.fontWeight ? { fontWeight: headline.fontWeight } : {}),
            ...(headline?.fontSize ? { fontSize: headline.fontSize } : {}),
          }}
        >
          {headline?.text ?? METRICS_HEADLINE}
        </div>
        <div className={styles.metricsCards}>
          {METRIC_CARDS.map((card) => (
            <div
              key={card.label}
              className={styles.metricsCard}
              style={
                {
                  '--metric-fill': `${card.fill}%`,
                  '--metric-tone': plain ? METRICS_PLAIN_BAR : card.tone,
                } as CSSProperties
              }
            >
              <div className={styles.metricsCardHead}>
                <span>{card.label}</span>
                <span>{card.meta}</span>
              </div>
              <div className={styles.metricsValue}>{card.value}</div>
              <div className={styles.metricsTrack}>
                <span className={styles.metricsBar} />
              </div>
              <div className={styles.metricsLine}>{card.line}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (kind === 'words') {
    return (
      <div className={styles.slide} style={{ ...vars, justifyContent: 'center', gap: 18 }}>
        {[
          { text: 'Too many steps', size: 84, indent: 0 },
          { text: 'They wanted the map', size: 64, indent: 180 },
          { text: 'Nobody scrolled', size: 72, indent: 60 },
        ].map((line) => (
          <div key={line.text} style={{ fontSize: line.size, lineHeight: 1.05, marginLeft: line.indent, color: ink }}>
            {line.text}
          </div>
        ))}
      </div>
    );
  }
  if (kind === 'figure') {
    return (
      <div className={styles.slide} style={{ ...vars, backgroundColor: ink, justifyContent: 'center' }}>
        <div className={styles.slideFigure}>11 weeks</div>
        <div className={styles.slideNote} style={{ color: palette?.note ?? '#9C9CA6', maxWidth: 620 }}>
          Two rounds of testing, four rebuilds and one direction we threw away.
        </div>
      </div>
    );
  }
  if (kind === 'trio') {
    return (
      <div className={styles.slide}>
        <div className={styles.slideLabel}>Three directions</div>
        <div className={styles.slideRow}>
          {[PINK, BLUE, SAND].map((tone) => (
            <div key={tone} className={styles.slidePanel} style={{ backgroundColor: tone }} />
          ))}
        </div>
      </div>
    );
  }
  if (kind === 'flow') {
    return (
      <div className={styles.slide}>
        <div className={styles.slideLabel}>The final flow</div>
        <div className={styles.slideRow} style={{ alignItems: 'center', gap: 0 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={styles.slideFlowStep}>
              <div className={styles.slideScreen} style={{ borderColor: i === 3 ? BLUE : '#DDDAD3' }}>
                <span className={styles.slideScreenBar} style={{ backgroundColor: i === 3 ? BLUE : '#DDDAD3' }} />
              </div>
              {i < 3 ? <span className={styles.slideArrow} /> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (kind === 'pair') {
    return (
      <div className={styles.slide}>
        <div className={styles.slideLabel}>What changed</div>
        <div className={styles.slideRow}>
          <div className={styles.slidePanel} style={{ backgroundColor: '#E4E2DD' }} />
          <div className={styles.slidePanel} style={{ backgroundColor: MOSS }} />
        </div>
      </div>
    );
  }
  if (kind === 'mosaic') {
    const tones = [PINK, '#E4E2DD', BLUE, SAND, MOSS, INK, SUN, '#C9D6E8'];
    return (
      <div className={styles.slide} style={{ padding: 64 }}>
        <div className={styles.slideMosaic}>
          {tones.map((tone, i) => (
            <span key={i} className={styles.slideTile} style={{ backgroundColor: tone }} />
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className={styles.slide}>
      <div className={styles.slideLabel}>What is next</div>
      <div className={styles.slidePlan}>
        {[
          { w: 420, x: 0, tone: MOSS },
          { w: 300, x: 160, tone: SUN },
          { w: 380, x: 320, tone: BLUE },
          { w: 240, x: 540, tone: PINK },
          { w: 300, x: 700, tone: '#DDDAD3' },
        ].map((bar, i) => (
          <span key={i} className={styles.slidePlanBar} style={{ width: bar.w, marginLeft: bar.x, backgroundColor: bar.tone }} />
        ))}
      </div>
    </div>
  );
}

/**
 * A plate: the artifact itself, scaled down to fit a slot.
 *
 * 34 wide is the directory row's, and stays the default so the two rows that already call this do
 * not move. The scale is `width / 1280` — the artifact's own width — and the box keeps 16:9, both
 * driven from one variable so a caller only ever supplies the width.
 */
export function SlideThumb({
  kind,
  width = 34,
  palette,
  className,
}: {
  kind: SlideKind;
  width?: number;
  palette?: SlidePalette;
  /** The caller's own shape for the plate — a bento cell's radius, for one. Layout only. */
  className?: string;
}) {
  return (
    <span
      className={cn(styles.thumb, className)}
      style={{ '--thumb-w': `${width}px`, '--thumb-scale': `${width / 1280}` } as CSSProperties}
    >
      <span className={styles.thumbScale}>
        <SlideArt kind={kind} palette={palette} />
      </span>
    </span>
  );
}
